import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

// ============================================================================
// Component
// ============================================================================

/**
 * Aion override of the Stadium House ApartmentSyncSection.
 *
 * Same thin wrapper around the `apartment-sync` block, with a white surface
 * and top padding so the Floor Plans widget has breathing room under the
 * HeroSolidBG page header. The parent section is flush (width-only), which
 * butted the widget against the navy title panel.
 */
export function ApartmentSyncSection() {
  const [mapArmed, setMapArmed] = useState(false);
  const blockSlot = useBlockSlot('apartmentsyncsection.block', {
    defaultValue: 'apartment-sync',
  });

  return (
    <section
      className={`apartment-sync-section${mapArmed ? '' : ' map-scroll-block'}`}
      onMouseLeave={() => setMapArmed(false)}
    >
      <EditableToken path="apartmentsyncsection.block" as="div">
        {blockSlot ? (
          <SectionContextProvider sectionId={blockSlot.sectionId}>
            <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div className="apartment-sync-empty">No block selected</div>
        )}
      </EditableToken>
      {!mapArmed && (
        <button
          type="button"
          className="apartment-sync-arm"
          aria-label="Click to interact with the map"
          onClick={() => setMapArmed(true)}
        />
      )}

      <style>{`
        .apartment-sync-section {
          position: relative;
          width: 100%;
          background: #ffffff;
          padding-top: var(--vibe-spacing-xl);
        }
        .apartment-sync-arm {
          position: absolute;
          inset: 0;
          z-index: 10;
          border: 0;
          background: transparent;
          cursor: pointer;
        }
        .map-scroll-block iframe { pointer-events: none !important; }
        @media (min-width: 768px) {
          .apartment-sync-section {
            padding-top: var(--vibe-spacing-2xl);
          }
        }
        .apartment-sync-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncSection',
  displayName: 'Apartment Sync Section',
  tokens: {
    'apartmentsyncsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApartmentSyncSection;
